<template>
  <div v-if="visible" class="fixed-bottom-sheet">
    <v-sheet elevation="14">
      <div class="v-container--fluid px-2 px-sm-7 pb-1 pb-md-3 pt-2 pt-md-3">
        <v-row no-gutters justify="center" justify-md="space-between">
          <v-col
            cols="auto"
            md="4"
            class="mb-3 mb-md-0 mt-0 mt-md-2 w-100 d-flex justify-space-around align-end align-md-center"
          >
            <ShareButtons
              v-if="isShareVisible"
              :share-url="shareUrl"
              class="d-inline-block"
              :buttons-id="shareButtonsId"
              :icon-size="iconSize"
            />
            <p
              id="swr-step2-print"
              class="primary-link pointer d-inline-block mb-0 ml-3 f-14 font-weight-medium"
              @click="openPrint"
            >
              <v-icon :size="iconSize" color="szViolet"> $print </v-icon>
              {{ $t('label.print') }}
            </p>
            <p
              v-if="showDownloadPDF"
              class="primary-link pointer d-inline-block mb-0 ml-3 f-14 font-weight-medium"
            >
              <v-icon :size="iconSize" color="szViolet"> $filePdfBox </v-icon>
              {{ $t('label.downloadPDF') }}
            </p>
            <v-fade-transition :hide-on-leave="true">
              <button
                v-if="isSupported"
                id="swr-step2-copy-url"
                :key="copied ? $t('label.copied') : $t('label.copy')"
                class="fixed-bottom-sheet__copy primary-link pointer d-inline-block mb-0 ml-3 f-14 font-weight-medium"
                @click="copy(shareUrl)"
              >
                <v-icon color="szViolet" :size="iconSize">
                  {{ copied ? '$check' : '$copy' }}
                </v-icon>
                {{ copied ? $t('label.copied') : $t('label.copy') }}
              </button>
            </v-fade-transition>
          </v-col>
          <v-col
            cols="12"
            md="auto"
            align-self="center"
            class="fixed-bottom-sheet__right-btn-section"
          >
            <p
              v-if="isChosen"
              class="mb-2 mb-md-0 d-block d-md-inline-block mr-0 mr-md-5 text-center"
            >
              {{ $t('family.hasDonor') }}
            </p>
            <BaseButton v-if="isChosen" :to="{ name: ROUTE_NAMES.HOME }" block-mobile arrow>
              {{ $t('family.findOtherFamily') }}
            </BaseButton>
            <div v-else>
              <BaseButton
                id="swr-step2-family-choose"
                :loading="donorLoader"
                :disabled="donorLoader"
                class="my-3 my-md-0"
                block
                arrow
                @click="$emit('submit')"
              >
                {{ $t('family.chooseFamily') }}
              </BaseButton>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-sheet>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/widgets/BaseButton.vue'
import ShareButtons from '@/components/widgets/ShareButtons.vue'
import { useClipboard } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
// @ts-expect-error: Importing Vuex store
import { useStore } from 'vuex'
import ROUTE_NAMES from '@/constants/routeNames'

type Props = {
  visible?: boolean
  isChosen?: boolean
  iconSize?: number
}

withDefaults(defineProps<Props>(), {
  visible: false,
  isChosen: false,
  iconSize: 28
})

const route = useRoute()
const store = useStore()

const showDownloadPDF = ref(false)

// It uses Clipboard API and falls back to document.execCommand('copy') if not supported
const { copy, copied, isSupported } = useClipboard({ legacy: true })

const shareUrl = computed<string>(() => {
  if (!route.params) return ''
  const { familyEditionId, warehouseId } = route.params

  const url = new URL(window.location.href)
  const PUBLIC_PATH = import.meta.env.VITE_PUBLIC_PATH ?? '/'
  url.pathname = `${PUBLIC_PATH}rodzina/${familyEditionId}/${warehouseId}`

  return url.href
})

const donorLoader = computed<boolean>(() => store.getters['family/getDonorLoader'])

const isShareVisible = computed<boolean>(() => !store.getters['home/isInitStateBefore'])

const shareButtonsId = {
  fb: 'swr-step2-fb-share',
  msg: 'swr-step2-messenger-share'
} as const

const openPrint = () => {
  window.print()
}
</script>
